import React from 'react';
import { Popover } from 'antd';
import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';

type StatusMeta = {
  icon: React.ReactNode;
  color: string;
  label: string;
  description: string;
  // Failure states get a bordered, labeled pill instead of a bare icon so they're
  // impossible to miss next to the plain success checkmarks.
  emphasize?: boolean;
};

const STATUS_META: Record<string, StatusMeta> = {
  queued: { icon: <ClockCircleOutlined />, color: '#8c8c8c', label: 'Sending', description: 'The message is being sent.' },
  accepted: { icon: <ClockCircleOutlined />, color: '#8c8c8c', label: 'Sending', description: 'The message is being sent.' },
  sending: { icon: <ClockCircleOutlined />, color: '#8c8c8c', label: 'Sending', description: 'The message is being sent.' },
  sent: { icon: <CheckOutlined />, color: '#1890ff', label: 'Sent', description: 'The message was sent to the carrier.' },
  delivered: { icon: <CheckCircleOutlined />, color: '#52c41a', label: 'Delivered', description: "The message was delivered to the customer's phone." },
  received: { icon: <CheckCircleOutlined />, color: '#52c41a', label: 'Delivered', description: "The message was delivered to the customer's phone." },
  undelivered: {
    icon: <ExclamationCircleFilled />, color: '#ff4d4f', label: 'Not Delivered',
    description: "The carrier could not deliver this message to the customer's phone.", emphasize: true,
  },
  failed: {
    icon: <CloseCircleFilled />, color: '#ff4d4f', label: 'Failed',
    description: 'This message failed to send.', emphasize: true,
  },
};

/**
 * Small delivery-status indicator for an outbound SMS bubble: a plain checkmark/clock
 * icon for the normal send lifecycle, or an emphasized red pill for undelivered/failed
 * so problems stand out without hovering. A Popover on any state gives the full label
 * and explanation. Shared by every SMS conversation view (SMS Center, Call Widget,
 * Callboard, Sales).
 */
export const SmsDeliveryStatusIndicator: React.FC<{ status?: string | null }> = ({ status }) => {
  const meta = status ? STATUS_META[status.toLowerCase()] : undefined;
  if (!meta) return null;

  return (
    <Popover title={meta.label} content={meta.description} placement="top" overlayStyle={{ maxWidth: 240 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          color: meta.color,
          fontSize: meta.emphasize ? 11 : 12,
          fontWeight: meta.emphasize ? 700 : 400,
          cursor: 'default',
          ...(meta.emphasize
            ? {
              background: 'rgba(255,77,79,0.1)',
              border: '1px solid rgba(255,77,79,0.4)',
              borderRadius: 10,
              padding: '0 6px',
              lineHeight: '16px',
            }
            : {}),
        }}
      >
        {meta.icon}
        {meta.emphasize && meta.label}
      </span>
    </Popover>
  );
};

export default SmsDeliveryStatusIndicator;
